import type { ReactNode } from 'react';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ColumnWidthOutlined,
	DeleteOutlined,
	ImportOutlined,
	InboxOutlined,
	PlusCircleOutlined,
	RollbackOutlined,
	ScissorOutlined,
	SplitCellsOutlined,
	SwapOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type {
	MaterialMovementRow,
	MovementAction,
	MovementReportBar,
} from '../../redux/api/material-movements';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SHOP_TIMEZONE = 'America/Chicago';

interface ActionMeta {
	label: string;
	color: string;
	icon: ReactNode;
}

/** Ordered the way material flows through the shop. */
export const ACTION_META: Record<MovementAction, ActionMeta> = {
	received: { label: 'Received', color: 'blue', icon: <InboxOutlined /> },
	cut: { label: 'Cut', color: 'orange', icon: <ScissorOutlined /> },
	consumed: {
		label: 'Fully consumed',
		color: 'default',
		icon: <CheckCircleOutlined />,
	},
	scrapped: { label: 'Scrapped', color: 'red', icon: <DeleteOutlined /> },
	remnant_returned: {
		label: 'Remnant returned',
		color: 'green',
		icon: <RollbackOutlined />,
	},
	job_returned: {
		label: 'Returned from job',
		color: 'cyan',
		icon: <ImportOutlined />,
	},
	split: { label: 'Split', color: 'purple', icon: <SplitCellsOutlined /> },
	added: { label: 'Added', color: 'geekblue', icon: <PlusCircleOutlined /> },
	moved: { label: 'Moved', color: 'gold', icon: <SwapOutlined /> },
	status_changed: {
		label: 'Status changed',
		color: 'default',
		icon: <SyncOutlined />,
	},
	length_adjusted: {
		label: 'Length adjusted',
		color: 'magenta',
		icon: <ColumnWidthOutlined />,
	},
	deleted: { label: 'Deleted', color: 'default', icon: <CloseCircleOutlined /> },
};

export const ACTION_ORDER = Object.keys(ACTION_META) as MovementAction[];

/** Always shown as summary cards, even at zero, so an empty day reads as empty. */
export const CORE_ACTIONS: MovementAction[] = [
	'received',
	'cut',
	'remnant_returned',
	'job_returned',
	'moved',
];

export const actionLabel = (row: Pick<MaterialMovementRow, 'action' | 'action_label'>) =>
	ACTION_META[row.action]?.label ?? row.action_label ?? row.action;

export const formatShopTime = (iso: string, format = 'MMM D, h:mm A') =>
	dayjs(iso).tz(SHOP_TIMEZONE).format(format);

export const formatInches = (value: string | null | undefined) =>
	value === null || value === undefined ? null : `${Number(value).toFixed(2)}"`;

/** Before → after when the length changed, otherwise the one known value. */
export const formatLengthChange = (row: MaterialMovementRow) => {
	const before = formatInches(row.length_before);
	const after = formatInches(row.length_after);
	if (before && after && before !== after) return `${before} → ${after}`;
	return after ?? before ?? '—';
};

export type LocationFormatter = (text: string) => string;

/** Shows the saved label unchanged. */
export const asIs: LocationFormatter = (text) => text;

/**
 * Saved movement labels are passed through `format`. Whether the bar moved is
 * decided on the saved labels: two places can shorten alike (old labels of a
 * rebuilt rack), and then the saved labels are shown so the move stays visible.
 */
export const formatLocationChange = (row: MaterialMovementRow, format: LocationFormatter = asIs) => {
	const from = row.from_location ? format(row.from_location) : null;
	const to = row.to_location ? format(row.to_location) : null;
	if (row.action === 'deleted') return from ? `Was at ${from}` : '—';
	if (row.from_location && row.to_location && row.from_location !== row.to_location) {
		return from === to ? `${row.from_location} → ${row.to_location}` : `${from} → ${to}`;
	}
	return to ?? from ?? '—';
};

export const operatorName = (row: MaterialMovementRow) =>
	row.performed_by?.name ?? 'Unknown';

export interface BarSummary {
	count: number;
	timeSpan: string;
	actionChain: string;
	netLength: string;
	netLocation: string;
	operators: string[];
	jobs: string[];
	parentBars: string[];
	hasBackfilled: boolean;
}

const unique = (values: (string | null | undefined)[]) =>
	Array.from(new Set(values.filter((v): v is string => Boolean(v))));

export const formatSpan = (first: string, last: string, showDate: boolean) => {
	const start = dayjs(first).tz(SHOP_TIMEZONE);
	const end = dayjs(last).tz(SHOP_TIMEZONE);
	const startText = start.format(showDate ? 'M/D h:mm A' : 'h:mm A');
	if (start.isSame(end, 'minute')) return startText;
	const endText = end.format(showDate && !start.isSame(end, 'day') ? 'M/D h:mm A' : 'h:mm A');
	return `${startText} – ${endText}`;
};

const collapseChain = (labels: string[]) => {
	const runs: { label: string; count: number }[] = [];
	labels.forEach((label) => {
		const last = runs[runs.length - 1];
		if (last && last.label === label) last.count += 1;
		else runs.push({ label, count: 1 });
	});
	return runs.map((r) => (r.count > 1 ? `${r.label} ×${r.count}` : r.label)).join(' › ');
};

const netLengthOf = (rows: MaterialMovementRow[]) => {
	const first = rows.find((r) => r.length_before !== null || r.length_after !== null);
	const last = [...rows].reverse().find((r) => r.length_after !== null || r.length_before !== null);
	const before = formatInches(first?.length_before ?? first?.length_after);
	const after = formatInches(last?.length_after ?? last?.length_before);
	if (before && after && before !== after) return `${before} → ${after}`;
	return after ?? before ?? '—';
};

/** Where the bar started and ended within the rows (saved labels, unformatted). */
export const netLocationEnds = (rows: MaterialMovementRow[]) => {
	const first = rows.find((r) => r.from_location || r.to_location);
	const last = [...rows].reverse().find((r) => r.to_location || r.from_location);
	return {
		start: first?.from_location ?? first?.to_location ?? null,
		end: last?.to_location ?? last?.from_location ?? null,
	};
};

const netLocationOf = (rows: MaterialMovementRow[], format: LocationFormatter) => {
	const lastRow = rows[rows.length - 1];
	if (lastRow?.action === 'deleted') {
		return lastRow.from_location ? `Was at ${format(lastRow.from_location)}` : '—';
	}
	const { start, end } = netLocationEnds(rows);
	if (start && end && start !== end) return `${format(start)} → ${format(end)}`;
	const only = end ?? start;
	return only ? format(only) : '—';
};

/** Net effect of a bar's movements in the range; rows arrive oldest first. */
export const summarizeBar = (
	bar: Pick<MovementReportBar, 'movements'>,
	{ showDate = true, formatLocation = asIs }: { showDate?: boolean; formatLocation?: LocationFormatter } = {}
): BarSummary => {
	const rows = bar.movements;
	if (rows.length === 0) {
		return {
			count: 0,
			timeSpan: '—',
			actionChain: '—',
			netLength: '—',
			netLocation: '—',
			operators: [],
			jobs: [],
			parentBars: [],
			hasBackfilled: false,
		};
	}
	return {
		count: rows.length,
		timeSpan: formatSpan(rows[0].occurred_at, rows[rows.length - 1].occurred_at, showDate),
		actionChain: collapseChain(rows.map(actionLabel)),
		netLength: netLengthOf(rows),
		netLocation: netLocationOf(rows, formatLocation),
		operators: unique(rows.map(operatorName)),
		jobs: unique(rows.map((r) => r.order_line_item?.name)),
		parentBars: unique(rows.map((r) => r.parent_bar_friendly_id)),
		hasBackfilled: rows.some((r) => r.is_backfilled),
	};
};

const TIMELINE_COLORS: Record<string, string> = {
	default: 'gray',
	geekblue: '#2f54eb',
};

export const timelineColor = (action: MovementAction) => {
	const color = ACTION_META[action]?.color ?? 'default';
	return TIMELINE_COLORS[color] ?? color;
};
